/**
 * FW-URL-11 spike: local passage retrieval mimicking Google site:url "keyword" snippets.
 * Deterministic scoring over crawl text — no SERP API calls.
 */
import type { SectionCoverageRow } from './api-types';

export const MAX_PASSAGE_CHARS = 200;

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'for', 'to', 'of', 'in', 'on', 'at', 'by', 'is', 'are',
  'was', 'were', 'be', 'with', 'from', 'as', 'it', 'its', 'your', 'you', 'what', 'how',
  'why', 'when', 'where', 'who', 'vs', 'best', 'top'
]);

export type PassageKind = 'heading' | 'heading_intro' | 'sentence' | 'paragraph' | 'meta';

export interface PassageSource {
  heading: string;
  headingPath?: string;
  sectionNumber?: number;
  /** Leaf heading level (1–6) when known; meta passages omit. */
  headingLevel?: number | null;
  text: string;
  kind: PassageKind;
  /** Lower = earlier on page */
  ordinal: number;
  isNonTopical?: boolean;
}

export interface KeywordPassageMatch {
  keyword: string;
  passage: string;
  passageHeading: string;
  passageHeadingLevel?: number | null;
  sectionNumber?: number;
  kind: PassageKind;
  score: number;
  matchedTokens: string[];
}

/** Body text for UI — strips heading prefix so it is not shown twice. */
export function passageBodyForDisplay(
  heading: string,
  passage: string,
  kind: PassageKind
): string {
  const h = (heading || '').trim();
  let body = (passage || '').trim();
  if (!body) return '';
  if (kind === 'heading') return '';

  if (h) {
    const lowerH = h.toLowerCase();
    const lowerB = body.toLowerCase();
    if (lowerB === lowerH) return '';
    if (lowerB.startsWith(lowerH)) {
      body = body.slice(h.length).replace(/^[\s.:;,\-–—]+/, '').trim();
    }
  }

  return body;
}

export function passageHeadingLevelLabel(
  level: number | null | undefined,
  kind: PassageKind | undefined
): string | null {
  if (kind === 'meta') return 'Meta';
  if (level != null && level >= 1 && level <= 6) return `H${level}`;
  return null;
}

export interface KeywordPassageEvidence {
  keyword: string;
  rivalPosition?: number;
  focalPosition?: number | null;
  searchVolume?: number;
  rival: KeywordPassageMatch | null;
  focal: KeywordPassageMatch | null;
  queryFamilyMismatch: boolean;
  mismatchNote: string | null;
}

function normalize(text: string): string {
  return text.toLowerCase().trim().replace(/\s+/g, ' ');
}

export function keywordTokens(keyword: string): string[] {
  return normalize(keyword)
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 2 && !STOP_WORDS.has(t));
}

function meaningfulQueryTokens(query: string): string[] {
  return normalize(query)
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 3 && !STOP_WORDS.has(t));
}

/**
 * The opening sentence of a passage, or its first 160 characters.
 *
 * Exported because the review surface's Missing sections card needs the same
 * thing off a rival's section body, and a second implementation of "where does
 * this sentence end" is how two parts of one card come to disagree about what
 * a rival said.
 */
export function extractFirstSentence(body: string): string {
  const trimmed = body.replace(/\s+/g, ' ').trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^(.{20,}?[.!?])(?:\s|$)/);
  return match ? match[1].trim() : trimmed.slice(0, 160).trim();
}

function splitSentences(body: string): string[] {
  const trimmed = body.replace(/\s+/g, ' ').trim();
  if (!trimmed) return [];
  return trimmed
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 20);
}

function keywordHasCostIntent(keyword: string, tokens: string[]): boolean {
  const kw = normalize(keyword);
  return (
    tokens.some((t) => ['cost', 'price', 'pricing', 'rate'].includes(t)) ||
    kw.includes('how much') ||
    kw.includes('cost')
  );
}

function isCostSection(heading: string): boolean {
  const h = normalize(heading);
  return h.includes('cost') || h.includes('how much') || h.includes('price');
}

/** Meta title + description — often matches Google site: snippet (section 0). */
export function buildMetaPassages(
  title: string,
  description: string,
  h1?: string | null
): PassageSource[] {
  const passages: PassageSource[] = [];
  const desc = description.trim();
  const ttl = title.trim();
  const h1Text = String(h1 || '').trim();

  if (desc) {
    passages.push({
      heading: ttl || h1Text || 'Page description',
      sectionNumber: 0,
      text: desc,
      kind: 'meta',
      ordinal: 0
    });
    if (ttl) {
      passages.push({
        heading: ttl,
        sectionNumber: 0,
        text: `${ttl}. ${desc}`,
        kind: 'meta',
        ordinal: 0
      });
    }
  } else if (ttl) {
    // Title alone still proves keyword presence (common when meta description is empty).
    passages.push({
      heading: ttl,
      sectionNumber: 0,
      text: ttl,
      kind: 'meta',
      ordinal: 0
    });
  }

  if (h1Text && h1Text.toLowerCase() !== ttl.toLowerCase()) {
    passages.push({
      heading: h1Text,
      sectionNumber: 0,
      headingLevel: 1,
      text: h1Text,
      kind: 'heading',
      ordinal: 0
    });
  }

  return passages;
}

/**
 * Focal-page passages from content-gap closest matches on rival section rows.
 * Works even when full focal crawl body is missing from the client analysis payload.
 */
export function buildPassagesFromClosestFocalSections(
  rows: SectionCoverageRow[]
): PassageSource[] {
  const seen = new Set<string>();
  const pseudo: SectionCoverageRow[] = [];

  for (const row of rows) {
    const heading = String(row.closestMyHeading || '').trim();
    if (!heading) continue;
    const key = heading.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    pseudo.push({
      sectionNumber: row.sectionNumber ?? pseudo.length + 1,
      sectionTotal: row.sectionTotal ?? 1,
      pagePosition: row.pagePosition || `${pseudo.length + 1}/?`,
      competitorHeading: heading,
      competitorHeadingPath: row.closestMyHeadingPath || heading,
      competitorHeadingLevel: row.closestMyHeadingLevel ?? undefined,
      sectionCovered: true,
      sectionSimilarity: row.sectionSimilarity || 0,
      closestMyHeading: null,
      closestMyHeadingPath: null,
      closestMyText: null,
      absentTerms: [],
      competitorText: String(row.closestMyText || '').trim(),
      isNonTopical: !!row.isNonTopical
    });
  }

  return buildPassagesFromSectionRows(pseudo);
}

function truncatePassage(text: string, max = MAX_PASSAGE_CHARS): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${base}…`;
}

/** Build passage candidates from rival section coverage rows. */
export function buildPassagesFromSectionRows(rows: SectionCoverageRow[]): PassageSource[] {
  const passages: PassageSource[] = [];

  for (const row of rows) {
    const heading = (row.competitorHeading || '').trim();
    const body = (row.competitorText || '').trim();
    const sectionNumber = row.sectionNumber ?? 999;
    const nonTopical = !!row.isNonTopical;
    const headingPath = row.competitorHeadingPath || heading;
    const headingLevel = row.competitorHeadingLevel ?? null;
    const ordinal = sectionNumber;

    if (heading) {
      passages.push({
        heading,
        headingPath,
        sectionNumber,
        headingLevel,
        text: heading,
        kind: 'heading',
        ordinal,
        isNonTopical: nonTopical
      });
    }

    if (heading && body) {
      const firstSentence = extractFirstSentence(body);
      const intro = firstSentence ? `${heading}. ${firstSentence}` : heading;
      passages.push({
        heading,
        headingPath,
        sectionNumber,
        headingLevel,
        text: intro,
        kind: 'heading_intro',
        ordinal,
        isNonTopical: nonTopical
      });
    }

    if (body) {
      for (const sentence of splitSentences(body)) {
        passages.push({
          heading,
          headingPath,
          sectionNumber,
          headingLevel,
          text: sentence,
          kind: 'sentence',
          ordinal,
          isNonTopical: nonTopical
        });
      }
      if (body.length >= 40) {
        passages.push({
          heading,
          headingPath,
          sectionNumber,
          headingLevel,
          text: body,
          kind: 'paragraph',
          ordinal,
          isNonTopical: nonTopical
        });
      }
    }
  }

  return passages;
}

/** Build passage candidates from focal page markdown (structured crawl). */
export function buildPassagesFromMarkdown(markdown: string): PassageSource[] {
  const passages: PassageSource[] = [];
  if (!markdown.trim()) return passages;

  const lines = markdown.split('\n');
  let currentHeading = '';
  let currentPath = '';
  let currentHeadingLevel: number | null = null;
  let sectionNumber = 0;
  let bodyLines: string[] = [];

  const flushSection = () => {
    const body = bodyLines.join(' ').replace(/\s+/g, ' ').trim();
    if (!currentHeading && !body) {
      bodyLines = [];
      return;
    }
    sectionNumber += 1;
    const heading = currentHeading || '(Intro)';
    const rows: SectionCoverageRow[] = [
      {
        sectionNumber,
        sectionTotal: sectionNumber,
        pagePosition: `${sectionNumber}/?`,
        competitorHeading: heading,
        competitorHeadingPath: currentPath || heading,
        competitorHeadingLevel: currentHeadingLevel ?? undefined,
        sectionCovered: true,
        sectionSimilarity: 0,
        closestMyHeading: null,
        closestMyHeadingPath: null,
        closestMyText: null,
        absentTerms: [],
        competitorText: body
      }
    ];
    passages.push(...buildPassagesFromSectionRows(rows));
    bodyLines = [];
  };

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushSection();
      currentHeading = headingMatch[2].trim();
      currentPath = currentHeading;
      currentHeadingLevel = headingMatch[1].length;
      continue;
    }
    const text = line.trim();
    if (text) bodyLines.push(text);
  }
  flushSection();

  return passages;
}

function matchedTokenList(keyword: string, passage: PassageSource): string[] {
  const tokens = keywordTokens(keyword);
  const hay = normalize(`${passage.heading} ${passage.text}`);
  return tokens.filter((t) => hay.includes(t));
}

export function scorePassageForKeyword(keyword: string, passage: PassageSource): number {
  if (passage.isNonTopical) return -100;

  const kw = normalize(keyword);
  const text = normalize(passage.text);
  const heading = normalize(passage.heading);
  const tokens = keywordTokens(keyword);
  let score = 0;

  if (heading.includes(kw)) score += 14;
  if (text.includes(kw)) score += 10;

  const allInHeading = tokens.length > 0 && tokens.every((t) => heading.includes(t));
  const allInText = tokens.length > 0 && tokens.every((t) => text.includes(t));

  if (allInHeading) score += 9;
  else if (allInText) score += 6;

  const partial = matchedTokenList(keyword, passage).length;
  score += partial * 1.5;

  if (passage.kind === 'heading') score += 4;
  else if (passage.kind === 'heading_intro') score += 5;
  else if (passage.kind === 'sentence') score += 1;
  else if (passage.kind === 'meta') score += 6;

  if (isCostSection(passage.heading) && !keywordHasCostIntent(keyword, tokens)) {
    score -= 14;
  }

  if (passage.sectionNumber != null) {
    score += Math.max(0, 5 - passage.sectionNumber * 0.35);
  } else {
    score += Math.max(0, 4 - passage.ordinal * 0.1);
  }

  return score;
}

export function findBestPassageForKeyword(
  keyword: string,
  passages: PassageSource[]
): KeywordPassageMatch | null {
  let best: { passage: PassageSource; score: number } | null = null;

  for (const passage of passages) {
    const score = scorePassageForKeyword(keyword, passage);
    if (score <= 0) continue;
    if (!best || score > best.score) {
      best = { passage, score };
    } else if (score === best.score) {
      const a = best.passage;
      if (
        passage.sectionNumber != null &&
        a.sectionNumber != null &&
        passage.sectionNumber < a.sectionNumber
      ) {
        best = { passage, score };
      }
    }
  }

  if (!best) return null;

  return {
    keyword,
    passage: truncatePassage(best.passage.text),
    passageHeading: best.passage.heading,
    passageHeadingLevel: best.passage.headingLevel ?? null,
    sectionNumber: best.passage.sectionNumber,
    kind: best.passage.kind,
    score: best.score,
    matchedTokens: matchedTokenList(keyword, best.passage)
  };
}

/** True when focal query and rival keyword/passage use divergent product modifiers. */
export function detectQueryFamilyMismatch(
  focalQuery: string,
  keyword: string,
  rivalPassage: string | null
): { mismatch: boolean; note: string | null } {
  const focalDistinct = meaningfulQueryTokens(focalQuery).filter(
    (t) => !['insurance', 'mechanic', 'mechanics', 'shop', 'business', 'liability'].includes(t)
  );
  if (!focalDistinct.length) {
    return { mismatch: false, note: null };
  }

  const kwNorm = normalize(keyword);
  const passageNorm = normalize(rivalPassage || '');
  const missingOnRival = focalDistinct.filter(
    (t) => !kwNorm.includes(t) && !passageNorm.includes(t)
  );
  const rivalAuto = kwNorm.includes('auto') || passageNorm.includes('auto');
  const focalMobile = focalDistinct.includes('mobile');

  if (focalMobile && rivalAuto && missingOnRival.includes('mobile')) {
    return {
      mismatch: true,
      note:
        'Focal page targets mobile mechanics; rival passage emphasizes auto mechanic — compare with caution.'
    };
  }

  if (missingOnRival.length >= 2) {
    return {
      mismatch: true,
      note: `Focal query emphasizes "${missingOnRival.slice(0, 2).join(', ')}" but rival keyword/passage does not.`
    };
  }

  return { mismatch: false, note: null };
}

export interface ComputeKeywordPassageEvidenceInput {
  keywords: Array<{
    keyword: string;
    rivalPosition?: number;
    focalPosition?: number | null;
    searchVolume?: number;
  }>;
  focalQuery?: string | null;
  rivalPassages: PassageSource[];
  focalPassages: PassageSource[];
}

export function computeKeywordPassageEvidence(
  input: ComputeKeywordPassageEvidenceInput
): KeywordPassageEvidence[] {
  const focalQuery = (input.focalQuery || '').trim();

  return input.keywords.map((row) => {
    const rival = findBestPassageForKeyword(row.keyword, input.rivalPassages);
    const focal = findBestPassageForKeyword(row.keyword, input.focalPassages);
    const { mismatch, note } = detectQueryFamilyMismatch(
      focalQuery,
      row.keyword,
      rival?.passage ?? null
    );

    return {
      keyword: row.keyword,
      rivalPosition: row.rivalPosition,
      focalPosition: row.focalPosition,
      searchVolume: row.searchVolume,
      rival,
      focal,
      queryFamilyMismatch: mismatch,
      mismatchNote: note
    };
  });
}
